// Ref: https://css-tricks.com/css-grid-in-ie-faking-an-auto-placement-grid-with-gaps/

// ------------------------------------
// MVP implementation

// TODO 	potentially defer to `layout` prop if no `layouts` match,
// 			reverting back to default auto-layout?
// 				-- e.g. specifying both layout AND layouts
// TODO 	support opt-OUT equal height for segments in rows
//				-- plus DOCS for how to enable propagate equal heights within segment wrappers (either display: flex or height: 100%)
// TODO 	for bp queries, can we have a type export from dd-bpc for the different/valid queries?
// TODO 	unit tests
// TODO 	integration tests
// TODO 	flag for opting out of responsive/bpc behaviour? Just purely layout?
// TODO 	prop for aligning content within segments? like vertically centre align when equal heights stretched

// ------------------------------------
// Future package-admin

// TODO 	probably deprecate inline prop? is there actually a use-case for this?
// TODO 	potentially deprecate useThisSize? Or? should align with decisions inside dd-bpc whether its kept there or not
// TODO 	support CSS border styles within the grid? somehow?
// 				-- including sub-options like including perimeter borders, or inner borders only (if possible)
// 				-- (is this potentially out-of-scope for this package...?)
// TODO 	can we remove classnames dependency? (yes!)

// ------------------------------------
// Known bugs

// TODO 	fully validate IE11 support; it's kinda the whole point we're using the CSS-Tricks implementation (it does work in IE11!)
// 				-- looks to be a discrepancy bug with the dynamic responsive example? is this replicable?
// TODO 	edge-to-edge DSL; is this a problem? (no overflow hidden pushes out width by the padding offset?)
// TODO 	overflow: hidden - is this absolutely necessary? Seems like a limitation?

// ------------------------------------------

import React from 'react';

import cx from 'classnames';

import safeGet from 'lodash/get';

import {
	BreakpointContainer,
	resolveBp,
} from '@deloitte-digital-au/dd-breakpoint-container';

import * as S from './DynamicSubLayout.style';

// ------------------------------------
// Types

type LayoutAlign = 'left' | 'right' | 'center' | 'between';
type LayoutGapObject = { x: number; y: number };
type LayoutGap = number | LayoutGapObject;

type LayoutObject = {
	bp: string;
	layout?: string;
	align?: LayoutAlign;
	gap?: LayoutGap;
};

export interface IProps {
	/** Feed in a named breakpoint from another BreakpointContainer, instead of using a new one. */
	useThisBp?: string;

	/** Override component behaviour according to the supplied pixel value; component won't use its own BreakpointContainer. */
	useThisSize?: number;

	/** Breakpoint name from which to stack segments vertically (for mobile layouts). 'Stack from this breakpoint (inclusive) and below'. Opt-out by passing `null`. */
	stackAt?: string | number | null;

	/** CSS marging string applied to component's container. */
	margin?: string;

	/** Vertical/horizontal spacing between segments in the layout. */
	gap?: LayoutGap;

	/** Align items within the layout; left, right, center, or between. */
	align?: LayoutAlign;

	/** Opt-out of inherent width: 100%; changes wrapper div to `display: inline-block`. */
	inline?: boolean;

	/** Updates current layout order by toggling `flex-direction`. */
	direction?: string;

	/** Reverses current layout order by toggling `flex-direction`. */
	reverse?: boolean;

	/** Layout query string; either a fraction or valid CSS width e.g. `${number}px`, percentage, or calc() expression. */
	layout?: string;

	/** Array of layouts for dynamic, responsive layouts. Supercedes `layout` prop. */
	layouts?: LayoutObject[];

	/** Passthrough `debug` flag to <BreakpointContainer/> */
	debug?: boolean;

	children: React.ReactNode;

	leftPadding?: number;

	rightPadding?: number;

	customCss?: string;
}

// ------------------------------------
// Core functions

/**
 * Find the most relevant layout according to current breakpoint.
 *
 * @param layouts - Responsive layout objects.
 * @param defaultLayout - Default layout object.
 * @param size - Module size in px, or breakpoint name.
 */
const getResponsiveLayout = (
	layouts: LayoutObject[],
	defaultLayout: LayoutObject,
	size: number | string,
): LayoutObject =>
	layouts.reduce((acc, layout) => {
		if (resolveBp(layout.bp, size)) {
			// Combined with defaultLayout to allow for
			// partial layout specifications, with fallback
			// values according to default layout
			return { ...defaultLayout, ...layout };
		} else {
			return acc;
		}
		// (`defaultLayout` is used as accumulator as an overall fallback,
		// and so any ommitted values have a set of 'default' values)
	}, defaultLayout);

// ------------------------------------

/**
 * @param currentLayout - Current layout object.
 * @param idx - Index.
 * @returns {string} A CSS expression: for example a px value, percentage, or calc.
 */
const getSegmentWidth = (
	currentLayout: LayoutObject,
	idx: number,
): string | undefined => {
	if (!currentLayout || !currentLayout.layout) return;

	// Normalise comma/whitespace separation to just commas
	// Note we can't accept just whitespace separation, because
	// a segment width may be a CSS calc() expression which will
	// also contain whitespaces; hence the comma separation requirement
	const layoutWidths = currentLayout.layout
		.trim()
		.replace(/,\s/g, ',')
		.split(',');
	let layoutIdx = idx;

	// Check if segment 'overflows' defined layout
	// If it does, find which segment/layout index
	// it corresponds to for wrapping purposes
	if (idx + 1 > layoutWidths.length) {
		layoutIdx = (idx + 1) % layoutWidths.length;
		// If modulo returns 0, it means its ought to in fact
		// be placed in the last position of the layout
		layoutIdx = layoutIdx === 0 ? layoutWidths.length : layoutIdx;
		// Re-correct for index
		layoutIdx -= 1;
	}

	// Get layout definition for the specificed segment
	const segmentWidth = layoutWidths[layoutIdx].trim();
	let widthCSS;

	// If it's a fraction, convert to a percentage
	if (!!segmentWidth.match(/^[0-9]+\/[0-9]+$/)) {
		const [numerator, denominator] = segmentWidth
			.split('/')
			.map(num => parseInt(num));

		try {
			widthCSS = `${(numerator / denominator) * 100}%`;
		} catch (e) {
			console.error(
				'Invalid fraction:',
				segmentWidth,
				'parsed as - numerator:',
				numerator,
				'denominator:',
				denominator,
				'. Full error:',
				e,
			);
		}
	} else {
		// Else, we can otherwise assume the definition is a CSS expression
		widthCSS = segmentWidth;
	}

	return widthCSS;
};

// ------------------------------------

const DynamicSubLayout = ({
	stackAt = 's',
	margin,
	gap = 20,
	align = 'left',
	direction = 'row',
	inline = false,
	reverse = false,
	debug = false,
	layout,
	layouts,
	children,
	useThisBp,
	useThisSize,
	leftPadding,
	rightPadding,
	customCss,
}: IProps) => {
	// Component can defer to outside logic for bp or size
	// e.g. so this module could be driven by the browser breakpoint,
	// or another BreakpointContainer in a controlling component.
	// (By default this component will use its own BreakpointContainer)
	const overrideBpc = useThisBp ?? useThisSize;
	const shouldOverrideBpc = !!overrideBpc;

	const _renderSubLayout = (
		bpName: string | undefined,
		bpcSize: number | undefined,
		shouldOverrideBpc = false,
	): React.ReactNode => {
		const size = (overrideBpc ?? bpcSize ?? bpName) as string | number;
		const isColumn = direction === 'column';
		const isStacked = !!stackAt && resolveBp(`0, ${stackAt}`, size);
		const hasFixedLayout = !!layout && !layouts;
		const hasResponsiveLayout = !!layouts;

		let currentLayout = {
			gap,
			layout: `1/${React.Children.count(children) ?? '1'}`,
		} as LayoutObject;

		if (hasFixedLayout) {
			currentLayout.layout = layout;
		} else if (hasResponsiveLayout) {
			currentLayout = getResponsiveLayout(
				layouts as LayoutObject[],
				currentLayout,
				size,
			);
		}

		// Use align from current responsive layout; fallback to default align
		align = currentLayout?.align ?? align;

		const xGap = safeGet(gap, 'x') ?? gap;
		const yGap = safeGet(gap, 'y') ?? gap;

		return (
			<S.Wrapper
				className={cx('dynamicSubLayout', { '-stacked': isStacked })}
				{...{ debug, shouldOverrideBpc, size, margin, customCss }}
			>
				<S.Container {...{ yGap, xGap, align, reverse, isStacked, isColumn }}>
					{React.Children.map(children, (field, idx) => {
						// It's appropriate to use an index value as a key here because
						// this component never changes the order of its children, and there
						// have been side-effects trying to calculate dynamic unique IDs.
						// The index key doesn't cause a problem here because if the children
						// passed through to DSL are reordered or removed/changed, it's up
						// to the controlling component to maintain the unique keys.
						/* eslint-disable-next-line react/no-array-index-key */
						const key = idx;

						return (
							field && (
								<S.Segment
									width={getSegmentWidth(currentLayout, idx)}
									{...{
										key,
										debug,
										isStacked,
										yGap,
										xGap,
										leftPadding,
										rightPadding,
									}}
								>
									{field}
								</S.Segment>
							)
						);
					})}
				</S.Container>
			</S.Wrapper>
		);
	};

	return (
		<S.BpWrapper {...{ inline }}>
			{!shouldOverrideBpc ? (
				<BreakpointContainer noBpClasses {...{ debug }}>
					{(bpName, bpcSize) => _renderSubLayout(bpName, bpcSize)}
				</BreakpointContainer>
			) : (
				_renderSubLayout(useThisBp, useThisSize, true)
			)}
		</S.BpWrapper>
	);
};

export default DynamicSubLayout;
